import { protectedResourceAction } from '~/shell/utils/authUtil.server';
import { getLogger } from '../../../../logging/logger';
import { requireFormString } from '~/common/utils/formDataUtil';

const log = getLogger('add-article-to-inventory-route');
import { INVENTORY_DETAIL_FORM_FIELDS } from '~/features/inventory/inventoryformfields';
import { addArticleToInventory, createInventoryEmpty } from '~/features/inventory/inventory.server';
import { Button } from '~/common/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '~/common/components/ui/dialog';
import { cn } from '~/common/utils/styleUtil';
import { LucideFilePlus } from 'lucide-react';
import { useAllInventories } from '~/features/inventory/routes/resources/AllInventoryRoute';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/common/components/ui/tabs';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { InventorySelect } from '~/features/inventory/components/InventorySelect';
import type { IInventoryAddResponseDtoData, IInventoryLookupDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { parseWithZod } from '@conform-to/zod/v4';
import { createInventorySchema } from '~/features/inventory/schemas/createInventorySchema';
import { InventoryCreate } from '~/features/inventory/components/InventoryCreate';
import { setLatestInventoryIdOnContext } from '~/shell/globalContext.ts';

export const action = protectedResourceAction(async ({ t, request, api }) => {
  const formData = await request.formData();
  const articlesJson = requireFormString(formData, 'articles');
  const intent = formData.get(FORM_FIELDS.Intent);

  const articles: {
    mainArticleId: number;
    isExternalArticle: boolean;
    quantity: number;
  }[] = JSON.parse(articlesJson);

  if (intent === FORM_FIELDS.Create) {
    const submission = parseWithZod(formData, { schema: createInventorySchema });

    if (submission.status !== 'success') {
      return submission.reply();
    }

    try {
      const inventory = await createInventoryEmpty(api, submission.value.name);

      for (const article of articles) {
        await addArticleToInventory(api, inventory.id, article.mainArticleId, article.isExternalArticle, article.quantity);
      }

      setLatestInventoryIdOnContext(inventory.id);

      return Response.json({
        ok: true,
        message: t('Inventar "{{0}}" erstellt und Artikel hinzugefügt', { 0: submission.value.name })
      });
    } catch (e) {
      log.error('Error adding article to inventory', e);
      return Response.json({ ok: false, message: t('Hinzufügen des Artikels fehlgeschlagen') });
    }
  }

  if (intent === FORM_FIELDS.Select) {
    const selectedInventory = requireFormString(formData, INVENTORY_DETAIL_FORM_FIELDS.selectedInventoryId);
    const selectedInventoryId = Number.parseInt(selectedInventory);

    try {
      const result: IInventoryAddResponseDtoData[] = [];
      for (const article of articles) {
        result.push(
          await addArticleToInventory(
            api,
            selectedInventoryId,
            article.mainArticleId,
            article.isExternalArticle,
            article.quantity
          )
        );
      }

      setLatestInventoryIdOnContext(selectedInventoryId);

      if (result.length === 1 && result[0].alreadyExists) {
        return Response.json({
          ok: true,
          message: t('Artikel bereits im Inventar "{{0}}"', { 0: result[0].inventoryName })
        });
      }
      return Response.json({ ok: true, message: t('Artikel hinzugefügt') });
    } catch (e) {
      log.error('Error adding article to inventory', e);
      return Response.json({ ok: false, message: t('Hinzufügen des Artikels fehlgeschlagen') });
    }
  }

  throw new Error('unsupported intent');
});

export function AddArticleToInventoryButton({
  articles,
  size,
  className,
  disabled,
  onClose,
  singularText = true
}: Readonly<{
  className?: string;
  articles: { mainArticleId: number; isExternalArticle: boolean; quantity: number }[];
  size?: 'default' | 'lg';
  disabled?: boolean;
  onClose?: () => void;
  singularText?: boolean;
}>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useScreenSize();
  const { inventories, latestInventoryId, isLoading } = useAllInventories(articles);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {size === 'default' ? (
          <Button
            variant={'primaryOutline'}
            disabled={disabled}
            className={cn('h-9 w-9 px-0', className)}
            data-testid={`addtoinventory-button`}
          >
            <LucideFilePlus size={14} />
          </Button>
        ) : (
          <Button
            variant={'link'}
            className={cn('justify-start', className)}
            data-testid={`addtoinventory-button`}
            disabled={disabled}
          >
            {t(singularText ? 'Zum Inventar hinzufügen' : 'Alle zum Inventar hinzufügen')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent showFullscreenOnMobile data-testid={'article-to-inventory-dialog'}>
        <DialogHeader>
          <DialogTitle>{t('Inventare')}</DialogTitle>
        </DialogHeader>
        <InventoryTabs
          preselectedInventoryId={latestInventoryId}
          articles={articles}
          isMobile={isMobile}
          onClose={() => handleOpenChange(false)}
          inventories={inventories}
          isLoading={isLoading}
        />
      </DialogContent>
    </Dialog>
  );
}

function InventoryTabs({
  onClose,
  isMobile,
  articles,
  inventories,
  isLoading,
  preselectedInventoryId
}: Readonly<{
  onClose: () => void;
  isMobile: boolean;
  articles: { mainArticleId: number; isExternalArticle: boolean; quantity: number }[];
  inventories: IInventoryLookupDtoData[];
  isLoading: boolean;
  preselectedInventoryId?: number;
}>) {
  const { t } = useTranslation();

  return (
    <Tabs defaultValue={FORM_FIELDS.Select} className={'flex flex-col'}>
      <TabsList className={cn('grid w-full grid-cols-2')}>
        <TabsTrigger
          className={'whitespace-normal'}
          value={FORM_FIELDS.Select}
          disabled={inventories?.length === 0}
          data-testid={`addtoinventory-tab-select`}
        >
          {t('Zu Inventar hinzufügen')}
        </TabsTrigger>
        <TabsTrigger
          className={'whitespace-normal'}
          value={FORM_FIELDS.Create}
          data-testid={`addtoinventory-tab-create`}
        >
          {t('Neues Inventar erstellen')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value={FORM_FIELDS.Select} className={'desktop:min-h-[170px] flex-1'}>
        <InventorySelect
          preselectedInventoryId={preselectedInventoryId}
          onClose={onClose}
          isMobile={isMobile}
          articles={articles}
          inventories={inventories}
          isLoading={isLoading}
        />
      </TabsContent>
      <TabsContent value={FORM_FIELDS.Create} className={'desktop:min-h-[170px] flex-1'}>
        <InventoryCreate articles={articles} isMobile={isMobile} onClose={onClose} />
      </TabsContent>
    </Tabs>
  );
}
